import React from 'react';
import {DetailsField} from 'components';
import {RequirementStatus} from 'types';
import {KeywordsField} from 'components/DetailsSection/KeywordsField';
import {FaqPanelMode} from 'components/Faq/FaqPanel';
import {useQuestionCountsByEntityTypeQuery} from 'components/Faq/Faq.generated';
import useQuestionsPermissions from 'components/Faq/hooks/useQuestionsPermissions';
import {useTranslation} from 'react-i18next';
import {
	useEnumsTranslation,
	useRequirementsTranslation,
} from '../hooks/useRequirementsTranslation';
import {RequirementFromDetailsPage} from './requirementDetailsPage.types';

interface UseRequirementDetailsFieldsParams {
	requirement: RequirementFromDetailsPage;
	showAudit: boolean;
	questionSummaryData: ReturnType<
		typeof useQuestionCountsByEntityTypeQuery
	>['data'];
}

export function useRequirementDetailsFields({
	requirement,
	showAudit,
	questionSummaryData,
}: UseRequirementDetailsFieldsParams): DetailsField[][] {
	const {t} = useRequirementsTranslation();
	const {t: faqT} = useTranslation('common/faq');
	const {tStatus, tCategory} = useEnumsTranslation();
	const {shouldShowFaq} = useQuestionsPermissions();

	const attachmentFaqLine = React.useMemo(() => {
		const attachmentField = {
			data: requirement.attachments,
			type: 'attachments' as const,
			label: t('Attachments'),
		};
		return shouldShowFaq
			? [
					attachmentField,
					{
						data: {
							...questionSummaryData?.questionCountsByEntityType?.find(
								t => t.entityId === requirement.id,
							),
							faqState: {
								mode: FaqPanelMode.RequirementDetails,
								entity: requirement,
								showPanel: true,
							},
							text: faqT('FaqText'),
						},
						type: 'faq' as const,
						label: faqT('FaqLabel'),
					},
			  ]
			: [attachmentField];
	}, [
		faqT,
		questionSummaryData?.questionCountsByEntityType,
		requirement.attachments,
		requirement.id,
		shouldShowFaq,
		t,
	]);

	const initialFieldRows = React.useMemo((): DetailsField[][] => {
		const StatusField: DetailsField[] = [];

		if ([RequirementStatus.Approval].includes(requirement.status)) {
			const StatusFieldMeta: DetailsField = {
				data: '',
				type: 'array',
				label: t('Approver'),
			};

			StatusField.push({
				...StatusFieldMeta,
				data: (requirement.approvalWorkFlow?.approverRefs ?? []).map(
					x => x.name,
				),
			});
		}

		const fields: DetailsField[][] = [
			[
				{
					data: requirement.name,
					type: 'text',
					label: t('Name'),
					audit: showAudit,
					auditFieldName: 'name',
				},
				{
					data: requirement.requirementId,
					type: 'text',
					label: t('RequirementId'),
					audit: showAudit,
					auditFieldName: 'requirementId',
				},
				{
					data:
						requirement.status === RequirementStatus.Final
							? requirement.version
							: '-',
					type: 'text',
					label: t('Versionsinfo'),
					audit: showAudit,
					auditFieldName: 'version',
				},
				{
					data: tStatus(requirement.status),
					type: 'text',
					label: t('Status'),
					audit: showAudit,
					auditFieldName: 'status',
				},
				...StatusField,
				{
					data: tCategory(requirement.category),
					type: 'text',
					label: t('RequirementCategoryTagPickerLabel'),
					audit: showAudit,
					auditFieldName: 'category',
				},
			],
			[
				{
					data: requirement.definition,
					type: 'richtext',
					label: t('Definition'),
					audit: showAudit,
					auditFieldName: 'definition',
				},
			],
			[
				{
					data: requirement.vexClusters,
					type: 'array',
					label: t('VexCluster'),
					audit: showAudit,
					auditFieldName: 'vexClusterRefs',
				},
				{
					data: requirement.systemLevels,
					type: 'array',
					label: t('SystemLevels'),
					audit: showAudit,
					auditFieldName: 'systemLevelRefs',
				},
				{
					data: requirement.markets,
					type: 'array',
					label: t('Markets'),
					audit: showAudit,
					auditFieldName: 'marketRefs',
				},
				{
					data: requirement.standardPlusPcmsClusters,
					type: 'array',
					label: t('StandardPlusPcmsClusters'),
					audit: showAudit,
					auditFieldName: 'standardPlusPcmsClusterRefs',
				},
				{
					data: requirement.keywords,
					label: t('Keywords'),
					audit: showAudit,
					auditFieldName: 'keywordRefs',
					render: KeywordsField,
				},
			],
			[
				{
					data: requirement.dateEffective,
					type: 'date',
					label: t('DateEffective'),
					audit: showAudit,
					auditFieldName: 'dateEffective',
				},
				{
					data: requirement.dateExpiration,
					type: 'date',
					label: t('DateExpiration'),
					audit: showAudit,
					auditFieldName: 'dateExpiration',
				},
			],
			[
				{
					data: requirement.driveVariants,
					type: 'array',
					label: t('DriveVariants'),
					audit: showAudit,
					auditFieldName: 'driveVariantRefs',
				},
				{
					data: requirement.engineVariants,
					type: 'array',
					label: t('EngineVariants'),
					audit: showAudit,
					auditFieldName: 'engineVariantRefs',
				},
				{
					data: requirement.gearboxVariants,
					type: 'array',
					label: t('GearboxVariants'),
					audit: showAudit,
					auditFieldName: 'gearboxVariantRefs',
				},
			],
			[
				{
					data: requirement.bodyworkVariants,
					type: 'array',
					label: t('BodyworkVariants'),
					audit: showAudit,
					auditFieldName: 'bodyworkVariantRefs',
				},
				{
					data: requirement.activeTypeMarkets,
					type: 'array',
					label: t('ActiveTypeMarkets'),
					audit: showAudit,
					auditFieldName: 'activeTypeMarketRefs',
				},
				{
					data: requirement.vehicleCategories,
					type: 'array',
					label: t('VehicleCategory'),
					audit: showAudit,
					auditFieldName: 'vehicleCategoryRefs',
				},
				{
					data: requirement.tags,
					type: 'array',
					label: t('Tags'),
					audit: showAudit,
					auditFieldName: 'tagRefs',
				},
			],
			[
				{
					data: requirement.createdBy,
					anonymous: requirement?.anonymous,
					type: 'personas',
					label: t('CreatedBy'),
				},
				{
					data: requirement.createdAt,
					type: 'date',
					label: t('CreatedAt'),
				},
				{
					data: requirement.modifiedBy,
					anonymous: requirement?.anonymous,
					type: 'personas',
					label: t('ModifiedBy'),
				},
				{
					data: requirement.modifiedAt,
					type: 'date',
					label: t('ModifiedAt'),
				},
			],
			attachmentFaqLine,
			[
				{
					data: requirement.documentReferences,
					type: 'references',
					label: t('DocumentReferences'),
				},
			],
		];

		if (requirement.parentRef || requirement.childrenRefs?.length) {
			const data = {
				parent: requirement.parentRef,
				childs: requirement.childrenRefs,
			};

			fields.push([
				{
					data,
					type: 'requirementRef',
					label: t('requirementReferences'),
				},
			]);
		}

		return fields;
	}, [
		requirement,
		t,
		showAudit,
		tStatus,
		tCategory,
		questionSummaryData,
		attachmentFaqLine,
	]);

	return initialFieldRows;
}
